import { Popover } from "@headlessui/react";
import { PlusIcon, SparklesIcon, TrashIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import React, { useEffect, useMemo, useState } from "react";

import { Badge, Small } from "@fronterahealth/frontera-ui-components";

import { PassageGoalType, PassageTargetTypeEdge } from "@api/graphql/types-and-hooks";
import { convertReadableString } from "@components/forms/utils";
import { useProgrammingReviewData } from "@providers/ProgrammingReviewProvider";

interface ProgrammingReviewGoalCardProps {
  goal: PassageGoalType;
  collapseAll: boolean;
  selectedTargetId: string | null;
  onAddCustomTarget: () => void;
  onAddSuggestedTarget: () => void;
  onSelectTarget: (targetId: string) => void;
  onSelectGoal: (goalId: string) => void;
  onDeleteTarget: (targetId: string) => void;
  isReadOnly: boolean;
}

export const ProgrammingReviewGoalCard: React.FC<ProgrammingReviewGoalCardProps> = ({
  goal,
  collapseAll,
  selectedTargetId,
  onAddCustomTarget,
  onAddSuggestedTarget,
  onSelectTarget,
  onSelectGoal,
  onDeleteTarget,
  isReadOnly,
}) => {
  const { searchGoalText, selectedProgramReviewGoal } = useProgrammingReviewData();
  const [isExpanded, setIsExpanded] = useState<boolean>(!collapseAll);
  const [hoveredTargetId, setHoveredTargetId] = useState<string | null>(null);

  // Sync collapse state when collapseAll changes
  useEffect(() => {
    setIsExpanded(!collapseAll);
  }, [collapseAll]);

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const handleGoalClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Select the goal (which should clear target selection)
    onSelectGoal(goal.id);
  };

  const handleTargetClick = (targetId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onSelectTarget(targetId);
  };

  const handleDeleteClick = (targetId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onDeleteTarget(targetId);
  };
  const escapeRegExp = (string: string) => string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  const highlightText = useMemo(() => {
    return (text: string | null | undefined) => {
      if (!text || !searchGoalText) return text;

      const escapedSearchText = escapeRegExp(searchGoalText);
      const regex = new RegExp(`(${escapedSearchText})`, "gi");

      const parts = text.split(regex);

      return parts.map((part, index) =>
        part.toLowerCase() === searchGoalText.toLowerCase() ? (
          <span key={index} className="bg-orange-200">
            {part}
          </span>
        ) : (
          part
        ),
      );
    };
  }, [searchGoalText]);

  const renderTargets = () => {
    return (
      <div className="px-3 pb-3 border-t border-limestone-100">
        <div className="flex items-center justify-between mb-2">
          <Small colorType="secondary">Targets</Small>
          {!isReadOnly && (
            <Popover className="relative">
              {({ close }) => (
                <>
                  <Popover.Button className="flex items-center justify-center w-6 h-6 rounded-sm hover:bg-limestone-100 transition-colors">
                    <PlusIcon className="w-4 h-4 text-limestone-600" />
                  </Popover.Button>

                  <Popover.Panel className="absolute right-0 z-10 mt-2 w-56 rounded-lg bg-white shadow-lg ring-1 ring-black/5 focus:outline-hidden">
                    <div className="py-1">
                      <button
                        type="button"
                        className="flex items-center w-full px-4 py-2 text-sm text-limestone-900 hover:bg-limestone-50 transition-colors"
                        onClick={() => {
                          onAddCustomTarget();
                          close();
                        }}
                      >
                        <PlusIcon className="w-4 h-4 mr-3 text-limestone-500" />
                        <span>Custom Target</span>
                      </button>
                      <button
                        type="button"
                        className="flex items-center w-full px-4 py-2 text-sm text-limestone-900 hover:bg-limestone-50 transition-colors"
                        onClick={() => {
                          onAddSuggestedTarget();
                          close();
                        }}
                      >
                        <SparklesIcon className="w-4 h-4 mr-3 text-blurple-500" />
                        <span>Suggested Target</span>
                      </button>
                    </div>
                  </Popover.Panel>
                </>
              )}
            </Popover>
          )}
        </div>

        {/* Render existing targets */}
        <div className="space-y-1">
          {goal.passageTargets?.edges?.map((edge: PassageTargetTypeEdge | null) => {
            const isSelected = selectedTargetId === edge?.node?.id;
            const isHovered = hoveredTargetId === edge?.node?.id && !isReadOnly;

            return (
              <div
                key={edge?.node?.id}
                className={`
                      group relative flex items-center justify-between px-3 py-2 rounded text-sm cursor-pointer transition-colors
                      ${isSelected ? "border-2 border-green-700" : "bg-limestone-50 hover:bg-limestone-100"}
                    `}
                onClick={(e) => handleTargetClick(edge?.node?.id ?? "", e)}
                onMouseEnter={() => setHoveredTargetId(edge?.node?.id ?? "")}
                onMouseLeave={() => setHoveredTargetId(null)}
              >
                <span className={`flex-1 ${isSelected ? "text-green-900 font-medium" : ""}`}>
                  {highlightText(edge?.node?.title ?? "")}
                </span>
                {isHovered && (
                  <button
                    type="button"
                    className="flex items-center justify-center w-6 h-6 rounded-sm hover:bg-red-100 transition-colors"
                    onClick={(e) => handleDeleteClick(edge?.node?.id ?? "", e)}
                    title="Delete target"
                  >
                    <TrashIcon className="w-4 h-4 text-red-600" />
                  </button>
                )}
              </div>
            );
          })}
          {(!goal.passageTargets?.edges || goal.passageTargets.edges.length === 0) && (
            <Small colorType="secondary" className="italic">
              No targets yet
            </Small>
          )}
        </div>
      </div>
    );
  };
  return (
    <div
      className={clsx(
        "border  rounded-lg mb-2 bg-white",
        selectedProgramReviewGoal?.id === goal.id ? "border-green-700 border-2 border-solid" : "border-limestone-200",
      )}
      onClick={handleGoalClick}
    >
      {isReadOnly && (
        <div className="flex flex-row p-3 pb-0">
          <Badge text={convertReadableString(goal.type)} appearance="neutral" />
        </div>
      )}
      {/* Goal Header */}
      <div
        className={clsx(
          "flex items-center justify-between p-3 rounded-t-lg cursor-pointer ",
          selectedProgramReviewGoal?.id === goal.id && !isReadOnly
            ? "bg-green-50 hover:bg-green-100 "
            : "hover:bg-limestone-50",
          isExpanded ? "rounded-t-lg" : "rounded-lg",
        )}
      >
        <div className="flex items-center gap-2 flex-1">
          <Small className="w-[90%] truncate ph-no-capture" displayType="loud" colorType="primary">
            <label title={goal.title || "Unknown goal name"}>{highlightText(goal.title)}</label>
          </Small>
        </div>
        <button
          type="button"
          className="text-limestone-500 hover:text-limestone-700 transition-transform"
          style={{ transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)" }}
          onClick={toggleExpanded}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      {/* Goal Targets - Collapsible */}
      {isExpanded && renderTargets()}
    </div>
  );
};
